import React, { useMemo } from 'react';
import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { PullRequestDetails, GitHubReview } from '@finvari/deliver';
import { formatDate } from '../../../utils/deploymentUtils';

interface ReviewTabProps {
  // commentCount is a document-only field present at runtime on the objects the contexts provide.
  pr: PullRequestDetails & { commentCount?: number };
}

const STATE_COLOR: Record<string, 'success' | 'error' | 'default' | 'warning'> = {
  APPROVED: 'success',
  CHANGES_REQUESTED: 'error',
  COMMENTED: 'default',
  DISMISSED: 'warning',
};

const ReviewTab: React.FC<ReviewTabProps> = ({ pr }) => {
  const gh = pr.pr;
  const author = gh.user?.login;
  const lastCommitDate = pr.lastCommit?.date ? new Date(pr.lastCommit.date).getTime() : 0;

  // Latest review per non-author reviewer, newest first.
  const latestByReviewer = useMemo(() => {
    const map = new Map<string, GitHubReview>();
    for (const r of pr.reviews ?? []) {
      const login = r.user?.login;
      if (!login || login === author) continue;
      const existing = map.get(login);
      if (!existing || new Date(r.submitted_at) >= new Date(existing.submitted_at)) {
        map.set(login, r);
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
    );
  }, [pr.reviews, author]);

  const commentCount = pr.commentCount ?? 0;
  const requested = gh.requested_reviewers ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>Requested reviewers</Typography>
        {requested.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {requested.map(r => <Chip key={r.login} size="small" variant="outlined" label={`${r.login} (pending)`} />)}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">None pending.</Typography>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" gutterBottom>Reviews</Typography>
        {latestByReviewer.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No reviews yet.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {latestByReviewer.map(r => {
              const stale = r.state === 'APPROVED' && lastCommitDate > 0 &&
                new Date(r.submitted_at).getTime() < lastCommitDate;
              return (
                <Box key={r.user?.login}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{r.user?.login}</Typography>
                    <Chip size="small" color={STATE_COLOR[r.state] ?? 'default'} label={r.state.replace('_', ' ')} />
                    <Typography variant="caption" color="text.secondary">{formatDate(r.submitted_at)}</Typography>
                    {stale && (
                      <Tooltip title="This approval predates the latest commit and may no longer apply.">
                        <Chip size="small" color="warning" variant="outlined" label="stale" />
                      </Tooltip>
                    )}
                  </Box>
                  {r.body && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {r.body}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Divider />

      <Typography variant="body2" color="text.secondary">{commentCount} comment{commentCount === 1 ? '' : 's'} on this PR.</Typography>
    </Box>
  );
};

export default ReviewTab;
